import { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { useAuthStore } from '../../stores'
import {
  assignmentService,
  clientService,
  programService,
  groupService,
  type AssignmentWithDetails,
} from '../../services'
import { AssignmentList, AssignmentForm } from '../../components/assignment'
import { Button, Input } from '../../components/common'
import { useToast } from '../../components/common/Toast'
import type {
  Client,
  Program,
  AssignmentInsert,
  AssignmentStatus,
  Group,
} from '../../types/database.types'

interface GroupWithMembers extends Group {
  member_count?: number
}

export function AssignmentsPage() {
  const [searchParams] = useSearchParams()
  const { profile } = useAuthStore()
  const toast = useToast()

  const [assignments, setAssignments] = useState<AssignmentWithDetails[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [programs, setPrograms] = useState<Program[]>([])
  const [groups, setGroups] = useState<GroupWithMembers[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [statusFilter, setStatusFilter] = useState<AssignmentStatus | ''>('')
  const [searchQuery, setSearchQuery] = useState('')

  const preselectedClientId = searchParams.get('client') || undefined
  const preselectedProgramId = searchParams.get('program') || undefined

  useEffect(() => {
    if (profile?.id) {
      loadData()
    }
  }, [profile?.id, statusFilter])

  useEffect(() => {
    if (preselectedClientId || preselectedProgramId) {
      setShowForm(true)
    }
  }, [preselectedClientId, preselectedProgramId])

  const loadData = async () => {
    if (!profile?.id) return
    setIsLoading(true)
    try {
      const [assignmentsData, clientsData, programsData, groupsData] = await Promise.all([
        assignmentService.getAssignments(profile.id, {
          status: statusFilter || undefined,
        }),
        clientService.getClients(profile.id),
        programService.getPrograms(profile.id),
        groupService.getGroupsWithCounts(profile.id),
      ])
      setAssignments(assignmentsData)
      setClients(clientsData)
      setPrograms(programsData)
      setGroups(groupsData)
    } catch (error) {
      console.error('Failed to load data:', error)
      toast.error('Failed to load assignments')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (data: AssignmentInsert) => {
    setIsSubmitting(true)
    try {
      await assignmentService.createAssignment(data)
      toast.success('Program assigned successfully')
      setShowForm(false)
      loadData()
    } catch (error) {
      toast.error('Failed to assign program')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBulkSubmit = async (assignments: AssignmentInsert[]) => {
    setIsSubmitting(true)
    try {
      const result = await assignmentService.createBulkAssignments(assignments)
      if (result.failed > 0) {
        toast.warning(
          `Assigned ${result.created} of ${result.created + result.failed} clients. ${result.failed} failed.`
        )
      } else {
        toast.success(`Program assigned to ${result.created} clients`)
      }
      setShowForm(false)
      loadData()
    } catch (error) {
      toast.error('Failed to assign program')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = async (assignment: AssignmentWithDetails) => {
    if (!confirm('Cancel this assignment? The client will lose access.')) {
      return
    }
    try {
      await assignmentService.cancelAssignment(assignment.id)
      toast.success('Assignment cancelled')
      loadData()
    } catch (error) {
      toast.error('Failed to cancel assignment')
    }
  }

  const handleComplete = async (assignment: AssignmentWithDetails) => {
    try {
      await assignmentService.completeAssignment(assignment.id)
      toast.success('Assignment marked as complete')
      loadData()
    } catch (error) {
      toast.error('Failed to complete assignment')
    }
  }

  const filteredAssignments = searchQuery
    ? assignments.filter(
        (a) =>
          a.client?.full_name
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          a.program?.title?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : assignments

  const canManageAssignments = profile?.subscription_tier !== 'free'

  if (!canManageAssignments) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <svg
          className="w-16 h-16 text-gray-400 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Assignments Locked
        </h1>
        <p className="text-gray-600 mb-6">
          Upgrade to Basic or higher to assign programs to clients.
        </p>
        <Link to="/dashboard/subscription">
          <Button>Upgrade Subscription</Button>
        </Link>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Assignments</h1>
          <p className="mt-1 text-gray-600">
            Manage program assignments to your clients
          </p>
        </div>
        <Button onClick={() => setShowForm(true)}>Assign Program</Button>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search by client or program..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as AssignmentStatus | '')
          }
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Active</p>
          <p className="text-2xl font-bold text-green-600">
            {assignments.filter((a) => a.status === 'active').length}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Completed</p>
          <p className="text-2xl font-bold text-blue-600">
            {assignments.filter((a) => a.status === 'completed').length}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Expired</p>
          <p className="text-2xl font-bold text-red-600">
            {assignments.filter((a) => a.status === 'expired').length}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total</p>
          <p className="text-2xl font-bold text-gray-900">
            {assignments.length}
          </p>
        </div>
      </div>

      {/* Assignment list */}
      <AssignmentList
        assignments={filteredAssignments}
        isLoading={isLoading}
        onCancelAssignment={handleCancel}
        onCompleteAssignment={handleComplete}
        emptyMessage={
          searchQuery
            ? 'No assignments match your search'
            : 'No assignments yet. Assign a program to get started!'
        }
      />

      {/* Assignment form modal */}
      {showForm && profile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowForm(false)}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Assign Program to Client
            </h2>
            <AssignmentForm
              clients={clients}
              programs={programs}
              groups={groups}
              providerId={profile.id}
              providerTier={profile.subscription_tier}
              onSubmit={handleSubmit}
              onBulkSubmit={handleBulkSubmit}
              onCancel={() => setShowForm(false)}
              isLoading={isSubmitting}
              preselectedClientId={preselectedClientId}
              preselectedProgramId={preselectedProgramId}
            />
          </div>
        </div>
      )}
    </div>
  )
}
